import { toRaw, unref } from 'vue'
import { Checkbox } from 'ant-design-vue'
import {
  ACTION_COLUMNS,
  ComponentCellName,
  MIN_WIDTH,
  MIN_WIDTH_SMALL,
  SELECT_COMPONENTS,
} from '../const'
import { type UseCheckboxCacheReturn } from './useCheckboxCache'
import type { /*ComputedRef,*/ Ref } from 'vue'
import type { TableProColumn, TableProGridEmit, TableProInstance, TableProProps } from '../types'

/**
 * 自动添加 checkbox/radio
 * @param tablePropsRef
 * @returns
 */
function autoAddChoosenElement(
  // tablePropsRef: ComputedRef<TableProProps>,
  columns: TableProProps['columns'] = [],
  checkboxConfig: TableProProps['checkboxConfig'],
  radioConfig: TableProProps['radioConfig'],
  tableRef: Ref<TableProInstance | null>,
  emit: TableProGridEmit,
  isCheckboxCacheEnabled: UseCheckboxCacheReturn['isCheckboxCacheEnabled'],
  createCheckboxCache: UseCheckboxCacheReturn['createCheckboxCache'],
  createAllCheckboxCache: UseCheckboxCacheReturn['createAllCheckboxCache'],
  deleteCheckboxCache: UseCheckboxCacheReturn['deleteCheckboxCache'],
  deleteAllCheckboxCache: UseCheckboxCacheReturn['deleteAllCheckboxCache']
) {
  // const { columns = [], checkboxConfig, radioConfig } = unref(tablePropsRef)
  // const hasCheckbox = Object.keys(checkboxConfig).length > 0
  // const hasRadioConfig = Object.keys(radioConfig).length > 0
  const isColumnsHasCheckbox = columns?.find((column) => column.type === 'checkbox')
  const isColumnsHasRadio = columns?.find((column) => column.type === 'radio')

  // column 可能是promise需要等有值后再执行，减少rerender
  if (!columns.length) return columns

  if (!isColumnsHasCheckbox && checkboxConfig.enabled) {
    columns.unshift({
      field: 'checkboxField',
      type: 'checkbox',
      fixed: 'left',
      slots: {
        header: (info: any) => {
          const { checked, indeterminate } = info
          return [
            <Checkbox
              indeterminate={indeterminate}
              checked={checked}
              onChange={async () => {
                const result = { ...info, checked: !checked }
                if (isCheckboxCacheEnabled.value) {
                  const { fullData } = unref(tableRef)!.getTableData()
                  if (result.checked) {
                    await createAllCheckboxCache(toRaw(fullData))
                  } else {
                    await deleteAllCheckboxCache({
                      deleteByPage: true,
                    })
                  }
                } else {
                  await unref(tableRef)?.toggleAllCheckboxRow()
                }
                emit('CheckboxAll', { ...result })
              }}
            />,
          ]
        },
        checkbox: (info: any) => {
          const { row, checked, indeterminate } = info
          return [
            <Checkbox
              indeterminate={indeterminate}
              checked={checked}
              onChange={async () => {
                const result = { ...info, checked: !checked }
                if (isCheckboxCacheEnabled.value) {
                  if (result.checked) {
                    await createCheckboxCache(toRaw(result.row))
                  } else {
                    await deleteCheckboxCache(toRaw(result.row))
                  }
                } else {
                  await unref(tableRef)?.toggleCheckboxRow(row)
                }
                emit('CheckboxChange', { ...result })
              }}
            />,
          ]
        },
      },
    })
  }

  if (!isColumnsHasRadio && radioConfig.enabled) {
    columns.unshift({
      field: 'radioField',
      type: 'radio',
      fixed: 'left',
    })
  }

  return columns
}

/**
 * 设置最小宽度
 * @param columns
 * @returns
 */
function setColumnMinWidth(columns: TableProColumn[]) {
  return columns.length
    ? columns?.map((column: TableProColumn) => {
        if (column.type && SELECT_COMPONENTS.includes(column.type)) {
          if (!column.width) column.width = MIN_WIDTH_SMALL
        } else if (column.field && ACTION_COLUMNS.includes(column.field)) {
          if (!column.minWidth) {
            if (!column.width) {
              column.minWidth = MIN_WIDTH_SMALL + 15
            } else {
              column.minWidth = column.width
            }
          }
        } else {
          if (!column.minWidth) {
            if (!column.width) {
              column.minWidth = MIN_WIDTH
            } else {
              column.minWidth = column.width
            }
          }
        }
        return column
      })
    : columns
}

/**
 * 包装单元格
 * @param columns
 * @returns
 */
function wrapperColumnSlot(columns: TableProColumn[]) {
  const handleWrapper = (column: TableProColumn) => {
    const { customRender } = column
    column['cellRender'] = {
      name: ComponentCellName,
      options: [{ customRender }],
    }
    return column
  }
  return columns.length
    ? columns.map((column: TableProColumn) => {
        const { children } = column
        if (children && children.length) {
          column.children = children.map((_column: TableProColumn) => handleWrapper(_column))
          return column
        } else {
          return handleWrapper(column)
        }
      })
    : columns
}

/**
 * 确保固定列无论什么情况都 visible，即使 fixed && !visible 也要显示
 *
 * vxetable 多级表头情况下，以及表头添加 visible 不生效，而添加 fixed 却生效，这里做特殊处理
 *
 * @param columns
 * @returns
 */
function setFixedColumnVisible(columns: TableProColumn[]) {
  const handler = (column: TableProColumn) => {
    if (column.fixed && !column.visible) {
      column.visible = true
    }
    return column
  }

  return columns.length
    ? columns?.map((column: TableProColumn) => {
        const { children } = column
        if (children && children.length) {
          column.children = children.map((_column: TableProColumn) => handler(_column))
          return handler(column)
        } else {
          return handler(column)
        }
      })
    : columns
}

/**
 * 抹平多级表头的 fixed 差异，比如二级表头有 fixed 但是一级没有
 * @param columns
 * @returns
 */
function setFixedMultiHeader(columns: TableProColumn[]) {
  let fixed: any[] = []

  const handler = (column: TableProColumn, idx?: number, parentColumn?: TableProColumn) => {
    // 如果传入父级表头那么进行处理，没传入则不用处理
    if (parentColumn) {
      // 父级表头如果有 fixed 那么依次赋值给子级
      if (Reflect.has(parentColumn, 'fixed')) {
        column.fixed = parentColumn.fixed
      } else {
        // 父级表头没有 fixed 那么依次将子级的 fixed 缓存
        fixed.push(column.fixed)
      }

      // 当循环来到最后一个子级时，将缓存的 fixed 过滤取值，如果有值则说明父级没有 fixed，则取出第一个给父级赋值，第一个如果没值那么说明子级也没有 fixed，那么赋值 undefined 即可
      if (idx === parentColumn.children!.length - 1 && fixed.length) {
        const fixedResult = fixed.filter((val) => val !== undefined)
        parentColumn.fixed = fixedResult.length ? fixedResult[0] : undefined
        // 回溯当前父级下的所有子级
        parentColumn.children!.forEach((childColumn) => (childColumn.fixed = parentColumn.fixed))
      }
    }
    return column
  }

  return columns.length
    ? columns?.map((column: TableProColumn) => {
        const { children } = column
        if (children && children.length) {
          fixed = []
          column.children = children.map((_column: TableProColumn, idx) =>
            handler(_column, idx, column)
          )
          return handler(column)
        } else {
          return handler(column)
        }
      })
    : columns
}

/**
 * 抹平多级表头的 visible 差异，比如二级表头有 visible 但是一级没有，
 * @param columns
 * @returns
 */
function setVisibleMultiHeader(columns: TableProColumn[]) {
  const handler = (column: TableProColumn, parentVisible: boolean | undefined) => {
    // If the parent is not visible, the child should not be visible
    if (parentVisible === false) {
      column.visible = false
    } else if (parentVisible === true && column.visible === undefined) {
      // If the parent is visible and the child's visibility is undefined, set it to true
      column.visible = true
    }

    // If the column has children, process them recursively
    if (column.children && column.children.length) {
      column.children.forEach((childColumn) => handler(childColumn, column.visible))

      // After processing children, if all children are false, set the parent to false
      if (column.children.every((child) => child.visible === false)) {
        column.visible = false
      }
    }

    return column
  }

  return columns.length ? columns.map((column) => handler(column, column.visible)) : columns
}
/**
 * 操作列数据，设置最小宽度，自动注入checkbox等
 * @param propsRef
 */
export function useColumns(
  // tablePropsRef: ComputedRef<TableProProps>,
  columns: TableProProps['columns'] = [],
  checkboxConfig: TableProProps['checkboxConfig'],
  radioConfig: TableProProps['radioConfig'],
  tableRef: Ref<TableProInstance | null>,
  emit: TableProGridEmit,
  isCheckboxCacheEnabled: UseCheckboxCacheReturn['isCheckboxCacheEnabled'],
  createCheckboxCache: UseCheckboxCacheReturn['createCheckboxCache'],
  createAllCheckboxCache: UseCheckboxCacheReturn['createAllCheckboxCache'],
  deleteCheckboxCache: UseCheckboxCacheReturn['deleteCheckboxCache'],
  deleteAllCheckboxCache: UseCheckboxCacheReturn['deleteAllCheckboxCache']
) {
  // const autoAddChoosenElementColumns = autoAddChoosenElement(tablePropsRef, tableRef, emit)
  const autoAddChoosenElementColumns = autoAddChoosenElement(
    columns,
    checkboxConfig,
    radioConfig,
    tableRef,
    emit,
    isCheckboxCacheEnabled,
    createCheckboxCache,
    createAllCheckboxCache,
    deleteCheckboxCache,
    deleteAllCheckboxCache
  )
  const setColumnMinWidthColumns = setColumnMinWidth(autoAddChoosenElementColumns)
  const setFixedMultiHeaderColumns = setFixedMultiHeader(setColumnMinWidthColumns)
  const setVisibleMultiHeaderColumns = setVisibleMultiHeader(setFixedMultiHeaderColumns)
  const setFixedColumnVisibleColumns = setFixedColumnVisible(setVisibleMultiHeaderColumns)
  return wrapperColumnSlot(setFixedColumnVisibleColumns)
}
